import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, ElementRef, HostListener } from '@angular/core';
import { Subject, forkJoin, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  TableService,
  DevArea,
  GroupUser,
  FacilityType,
  FacilityName,
} from '../../services/table.service';
import { AuthService } from '../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { ErrorHandler } from '../../utils/error-handler';

interface ColumnConfig {
  field: keyof DevArea | keyof FacilityType | keyof FacilityName;
  header: string;
  editable: boolean;
  lookup?: boolean;
  filterable?: boolean;
}

interface LookupData {
  owner: string[];
  ownerEmails: { name: string; email: string }[];
}

type AdminTableType = 'dev' | 'type' | 'name';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  standalone: false,
})
export class AdminComponent implements OnInit, OnDestroy {
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  private readonly adminExportTabNames: Record<string, string> = {
    dev: 'DevelopmentArea',
    type: 'EntityTypeAdmin',
    name: 'FacilityNameAdmin',
  };

  expandedTable: string | null = null;
  public dialogMessage: string = '';

  columnsDevArea: ColumnConfig[] = [
    { field: 'developmentArea', header: 'Development Area', editable: true, filterable: true },
    { field: 'owner', header: 'Owner(s)', editable: true, lookup: true, filterable: true },
    { field: 'isActive', header: 'Status', editable: false, filterable: true },
    { field: 'updatedBy', header: 'Updated By', editable: false, filterable: true },
  ];
  columnsFacilityType: ColumnConfig[] = [
    { field: 'type', header: 'Facility Type', editable: true, filterable: true },
    { field: 'createdBy', header: 'Created By', editable: false, filterable: true },
    { field: 'updatedBy', header: 'Updated By', editable: false, filterable: true },
  ];
  columnsFacilityName: ColumnConfig[] = [
    { field: 'developmentArea', header: 'Development Area', editable: true, filterable: true },
    { field: 'type', header: 'Facility Type', editable: true, filterable: true },
    { field: 'facilityName', header: 'Facility Name', editable: true, filterable: true },
    { field: 'createdBy', header: 'Created By', editable: false, filterable: true },
    { field: 'updatedBy', header: 'Updated By', editable: false, filterable: true },
  ];
  dataDevArea: DevArea[] = [];
  dataFacilityType: FacilityType[] = [];
  dataFacilityName: FacilityName[] = [];
  filteredDataDevArea: DevArea[] = [];
  filteredDataFacilityType: FacilityType[] = [];
  filteredDataFacilityName: FacilityName[] = [];
  selectedRowsDev: DevArea[] = [];
  selectedRowsType: FacilityType[] = [];
  showCancelDev = false;
  showCancelType = false;
  showCancelName = false;
  filterSearch: Record<AdminTableType, Record<string, string>> = {
    dev: {},
    type: {},
    name: {},
  };
  multiFilters: Record<AdminTableType, Record<string, string[]>> = {
    dev: {},
    type: {},
    name: {},
  };
  activeFilter = '';
  activeFilterTable: AdminTableType = 'dev';
  showFloatingFilter = false;
  dropdownPosition = { top: 0, left: 0 };
  lookupData: LookupData = { owner: [], ownerEmails: [] };
  private destroy$ = new Subject<void>();

  constructor(
    private tableService: TableService,
    private authService: AuthService,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.setActiveAccountIfNotSet();
    this.loadAzureGroupUsers();
    this.loadDevAreaData();
    this.loadFacilityTypeData();
    this.loadFacilityNameData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showFloatingFilter) {
      return;
    }

    const target = event.target as HTMLElement;
    const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
    const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
      target.parentElement?.classList.contains('filter-icon');

    if (!clickedInside && !clickedOnFilterIcon) {
      this.closeFloatingFilter();
    }
  }
  // This method ensures that an active account is set in MSAL if none is currently active.
public setActiveAccountIfNotSet(): void {
    const account =
      this.tableService['msalService'].instance.getActiveAccount();
    const allAccounts =
      this.tableService['msalService'].instance.getAllAccounts();
    if (!account && allAccounts.length > 0) {
      this.tableService['msalService'].instance.setActiveAccount(
        allAccounts[0]
      );
    }
  }
  // This method loads the Azure group users and populates the lookup data for owners.
  private async loadAzureGroupUsers(): Promise<void> {
    try {
      const users = await this.tableService.getAzureGroupUsers();
      this.lookupData.owner = users.map((u) => u.name);
      this.lookupData.ownerEmails = users;
    } catch (err) {}
  }
  // This method loads the development area data from the backend and prepares it for display.
  public async loadDevAreaData(): Promise<void> {
    try {
      const data = await this.tableService.getDevAreaAdminDetails();
      this.dataDevArea = data.map((row) => ({
        ...row,
        originalDevelopmentArea: row.developmentArea,
        isNew: false,
      }));
      this.applyTableFilter('dev');
    } catch (err) {
      this.toastr.error('Failed to load development area data.');
    }
  }
  // This method loads the facility type data from the backend and prepares it for display.
  public async loadFacilityTypeData(): Promise<void> {
    try {
      const data = await this.tableService.getFacilityTypeAdminDetails();
      this.dataFacilityType = data.map((item) => ({
        ...item,
        isNew: false,
        originalFacilityTypeName: item.type,
      }));
      this.applyTableFilter('type');
    } catch (err) {
      this.toastr.error('Failed to load facility type data.');
    }
  }
  // This method loads the facility name data from the backend and prepares it for display.
  public loadFacilityNameData(): void {
    this.tableService
      .getFacilityNameAdminDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.dataFacilityName = data.map((item) => ({
          ...item,
          createdBy: item.createdBy ?? '',
          updatedBy: item.updatedBy ?? '',
          originalFacilityName: item.facilityName,
          isNew: false,
        }));
        this.applyTableFilter('name');
      });
  }
  // This method toggles the expansion of the specified table type.
  public toggleExpand(type: string): void {
    this.expandedTable = this.expandedTable === type ? null : type;

    if (this.expandedTable !== type) {
      this.closeFloatingFilter();
    }

    if (this.expandedTable === type) {
      this.closeFloatingFilter();
    }
  }
  // This method handles the addition of a new row based on the specified type.
  public handleAdd(type: string): void {
    const user = this.authService.getUserName();

    if (type === 'type') {
      const newRow: FacilityType = {
        type: '',
        createdBy: user,
        updatedBy: user,
        isNew: true,
      };
      this.dataFacilityType.unshift(newRow);
      this.showCancelType = true;
      this.applyTableFilter('type');
    }

    if (type === 'name') {
      const newRow: FacilityName = {
        developmentArea: '',
        type: '',
        facilityName: '',
        createdBy: user,
        updatedBy: user,
        isNew: true,
      };
      this.dataFacilityName.unshift(newRow);
      this.showCancelName = true;
      this.applyTableFilter('name');
    }
  }
  // This method handles the cancellation of a new row based on the specified type.
  public handleCancel(type: string): void {
    const data = this.getDataByType(type);
    const index = data.findIndex((r) => r.isNew);
    if (index !== -1) data.splice(index, 1);
    this.setCancelFlag(type, false);
    this.applyTableFilter(type as AdminTableType);
  }
  // This method clears the selection of rows based on the specified type.
  public clearSelection(type: string): void {
    if (type === 'dev') this.selectedRowsDev = [];
    if (type === 'type') this.selectedRowsType = [];
  }
  // This method checks if a row is selected based on the specified type.
  public isRowSelected(row: any, type: string): boolean {
    if (type === 'dev') return this.selectedRowsDev.includes(row);
    if (type === 'type') return this.selectedRowsType.includes(row);
    return false;
  }
  // This method handles the saving of data based on the specified type.
  public handleSave(type: string): void {
    this.setCancelFlag(type, false);

    if (type === 'dev') this.saveDevAreaToBackend();
    if (type === 'type') this.saveFacilityTypeToBackend();
    if (type === 'name') this.saveFacilityNameToBackend();
  }
  // This method handles the deletion of selected rows based on the specified type.
  public async handleDelete(type: string): Promise<void> {
    if (type === 'dev') {
      if (!this.selectedRowsDev.length) {
        return;
      }

      this.dialogMessage = `Are you sure you want to delete ${this.selectedRowsDev.length} Development Area row(s)? This action cannot be undone.`;
      const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
        width: '400px',
        disableClose: true,
      });
      const confirmed = await firstValueFrom(dialogRef.afterClosed());
      if (!confirmed) {
        return;
      }

      const requests = this.selectedRowsDev.map((row) =>
        this.tableService.deleteDevAreaAdmin(row.developmentArea)
      );

      forkJoin(requests)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const successMessages: string[] = [];
            const errorMessages: string[] = [];

            res.forEach((r: any) => {
              const parsed = ErrorHandler.parseApiResult(r);
              if (parsed.success) {
                successMessages.push(parsed.message || 'Deleted.');
              } else {
                errorMessages.push(parsed.message || 'Delete failed.');
              }
            });

            if (successMessages.length > 0) {
              this.toastr.success(successMessages.join('\n'));
            }

            if (errorMessages.length > 0) {
              this.toastr.error(errorMessages.join('\n'));
            }

            this.loadDevAreaData();
            this.selectedRowsDev = [];
          },

          error: (err) => {
            const parsed = ErrorHandler.parseApiResult(err?.error || err);
            this.toastr.error(parsed.message || 'Failed to delete Development Area(s).');
          },
        });
    }
    if (type === 'type') {
      if (!this.selectedRowsType.length) {
        return;
      }

      this.dialogMessage = `Are you sure you want to delete ${this.selectedRowsType.length} Facility Type row(s)? This action cannot be undone.`;
      const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
        width: '400px',
        disableClose: true,
      });
      const confirmed = await firstValueFrom(dialogRef.afterClosed());
      if (!confirmed) {
        return;
      }

      const requests = this.selectedRowsType.map((row) =>
        this.tableService.deletefacilityTypeAdmin(row.type)
      );

      forkJoin(requests)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const successMessages: string[] = [];
            const errorMessages: string[] = [];

            res.forEach((r: any) => {
              const parsed = ErrorHandler.parseApiResult(r);
              if (parsed.success) {
                successMessages.push(parsed.message || 'Deleted.');
              } else {
                errorMessages.push(parsed.message || 'Delete failed.');
              }
            });

            if (successMessages.length > 0) {
              this.toastr.success(successMessages.join('\n'));
            }

            if (errorMessages.length > 0) {
              this.toastr.error(errorMessages.join('\n'));
            }

            this.loadFacilityTypeData();
            this.selectedRowsType = [];
          },
          error: (err) => {
            const parsed = ErrorHandler.parseApiResult(err?.error || err);
            this.toastr.error(parsed.message || 'Failed to delete Facility Type(s).');
          },
        });
    }
  }
  // This method handles the editing of a row based on the specified type.
  public handleEdit(row: any, type: string): void {
    if (type === 'dev' && row.owner && Array.isArray(row.owner)) {
      const emails = row.owner
        .map(
          (name: string) =>
            this.lookupData.ownerEmails.find((u) => u.name === name)?.email
        )
        .filter(Boolean);
      row.ownerEmail = emails.join(', ');
    }
    row.updatedBy = this.authService.getUserName();
    row.isModified = true;
    this.applyTableFilter(type as AdminTableType);
  }
  // This method adds an owner to a development area row based on the selected value from a dropdown.
  public addOwner(row: DevArea, event: Event): void {
    const selected = (event.target as HTMLSelectElement).value;
    if (!row.owner.includes(selected)) {
      row.owner.push(selected);
      this.handleEdit(row, 'dev');
    }
    (event.target as HTMLSelectElement).value = '';
  }
  // This method removes an owner from a development area row based on the specified owner name.
  public removeOwner(row: DevArea, owner: string): void {
    row.owner = row.owner.filter((o) => o !== owner);
    this.handleEdit(row, 'dev');
  }
  // This method checks if a field is editable based on the type, row, and field name.
  public isEditable(type: string, row: any, field?: string): boolean {
    if (type === 'dev' && field === 'developmentArea') return !!row.isNew;
    if (row.isNew) return true;
    if (type === 'name' && (field === 'developmentArea' || field === 'type'))
      return false;
    return true;
  }
  // This method retrieves the column configuration based on the specified type.
  public getColumnsByType(type: string): ColumnConfig[] {
    if (type === 'dev') return this.columnsDevArea;
    if (type === 'type') return this.columnsFacilityType;
    return this.columnsFacilityName;
  }
  // This method retrieves the data array based on the specified type.
  public getDataByType(type: string): any[] {
    if (type === 'dev') return this.dataDevArea;
    if (type === 'type') return this.dataFacilityType;
    return this.dataFacilityName;
  }
  // This method sets the cancel flag for the specified type, which determines whether the cancel button is shown.
  public setCancelFlag(type: string, value: boolean): void {
    if (type === 'dev') this.showCancelDev = value;
    if (type === 'type') this.showCancelType = value;
    if (type === 'name') this.showCancelName = value;
  }
  public openFloatingFilter(event: MouseEvent, type: AdminTableType, field: string): void {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilterTable === type && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const dropdownWidth = 280;
    const viewportPadding = 12;
    const preferredLeft = rect.left - 90;
    const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;

    this.dropdownPosition = {
      top: Math.max(viewportPadding, rect.bottom + 8),
      left: Math.min(Math.max(preferredLeft, viewportPadding), Math.max(viewportPadding, maxLeft)),
    };
    this.activeFilterTable = type;
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }
  public closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }
  public getFilterSearchValue(type: AdminTableType, field: string): string {
    return this.filterSearch[type][field] || '';
  }
  public setFilterSearchValue(type: AdminTableType, field: string, value: string): void {
    this.filterSearch[type][field] = value;
  }
  public getFilterOptions(type: AdminTableType, field: string): string[] {
    const rows = this.getDataByType(type).filter((row) => this.rowMatchesFilters(type, row, field));
    const searchValue = this.normalizeFilterValue(this.getFilterSearchValue(type, field));
    const options = [...new Set(rows.flatMap((row) => this.getRowValuesForField(row, field)))]
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right));

    if (!searchValue) {
      return options;
    }

    return options.filter((option) => this.normalizeFilterValue(option).includes(searchValue));
  }
  public toggleMultiFilter(type: AdminTableType, field: string, value: string, event: Event): void {
    if (!this.multiFilters[type][field]) {
      this.multiFilters[type][field] = [];
    }

    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      if (!this.multiFilters[type][field].includes(value)) {
        this.multiFilters[type][field].push(value);
      }
      return;
    }

    this.multiFilters[type][field] = this.multiFilters[type][field].filter((item) => item !== value);
  }
  public isChecked(type: AdminTableType, field: string, value: string): boolean {
    return this.multiFilters[type][field]?.includes(value) || false;
  }
  public applyFilter(type: AdminTableType): void {
    this.applyTableFilter(type);
    this.closeFloatingFilter();
  }
  public clearFilter(type: AdminTableType, field: string): void {
    this.multiFilters[type][field] = [];
    this.filterSearch[type][field] = '';
    this.applyTableFilter(type);
    this.closeFloatingFilter();
  }
  public clearAllFilters(type: AdminTableType): void {
    this.multiFilters[type] = {};
    this.filterSearch[type] = {};
    this.applyTableFilter(type);

    if (this.activeFilterTable === type) {
      this.closeFloatingFilter();
    }
  }
  public hasAnyFilterApplied(type: AdminTableType): boolean {
    return Object.values(this.multiFilters[type]).some((values) => (values || []).length > 0);
  }
  public isFilterActive(type: AdminTableType, field: string): boolean {
    return (this.multiFilters[type][field] || []).length > 0;
  }
  public getFilteredCount(type: AdminTableType): number {
    return this.getFilteredDataByType(type).length;
  }
  private getFilteredDataByType(type: AdminTableType): Array<DevArea | FacilityType | FacilityName> {
    if (type === 'dev') return this.filteredDataDevArea;
    if (type === 'type') return this.filteredDataFacilityType;
    return this.filteredDataFacilityName;
  }
  private applyTableFilter(type: AdminTableType): void {
    const filteredRows = this.getDataByType(type).filter((row) => this.rowMatchesFilters(type, row));

    if (type === 'dev') {
      this.filteredDataDevArea = filteredRows as DevArea[];
      return;
    }

    if (type === 'type') {
      this.filteredDataFacilityType = filteredRows as FacilityType[];
      return;
    }

    this.filteredDataFacilityName = filteredRows as FacilityName[];
  }
  private rowMatchesFilters(
    type: AdminTableType,
    row: DevArea | FacilityType | FacilityName,
    excludeField?: string
  ): boolean {
    return Object.entries(this.multiFilters[type]).every(([field, values]) => {
      if (field === excludeField || !values?.length) {
        return true;
      }

      return this.getRowValuesForField(row, field).some((value) => values.includes(value));
    });
  }
  private getRowValuesForField(row: DevArea | FacilityType | FacilityName, field: string): string[] {
    if (field === 'owner') {
      return (row as DevArea).owner || [];
    }

    if (field === 'isActive') {
      return [(row as DevArea).isActive === 1 ? 'Active' : 'Inactive'];
    }

    const value = (row as unknown as Record<string, unknown>)[field];
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter(Boolean);
    }

    const normalizedValue = String(value ?? '').trim();
    return normalizedValue ? [normalizedValue] : [];
  }
  private normalizeFilterValue(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }
  private buildDevAreaPayload(row: DevArea, userName: string): {
    developmentArea: string;
    owner: string;
    ownerEmail: string;
    userName: string;
    isActive: string;
  } {
    return {
      developmentArea: row.developmentArea,
      owner: row.owner.join(','),
      ownerEmail: row.ownerEmail,
      userName,
      isActive: String(row.isActive ?? 1),
    };
  }
  public toggleDevAreaStatus(row: DevArea): void {
    if (row.isStatusUpdating) {
      return;
    }

    const previousStatus = row.isActive ?? 0;
    const userName = this.authService.getUserName();
    row.isActive = previousStatus === 1 ? 0 : 1;
    row.isStatusUpdating = true;

    this.tableService
      .insertOrUpdateDevArea(this.buildDevAreaPayload(row, userName))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          row.isStatusUpdating = false;
          const parsed = ErrorHandler.parseApiResult(res);

          if (parsed.success) {
            row.isModified = false;
            row.updatedBy = userName;
            this.toastr.success(parsed.message || 'Development Area status updated.');
            this.loadDevAreaData();
            return;
          }

          row.isActive = previousStatus;
          this.toastr.error(parsed.message || 'Failed to update Development Area status.');
        },
        error: (err) => {
          row.isStatusUpdating = false;
          row.isActive = previousStatus;
          const parsed = ErrorHandler.parseApiResult(err?.error || err);
          this.toastr.error(parsed.message || 'Failed to update Development Area status.');
        },
      });
  }
  // This method saves the development area data to the backend, handling both new and modified rows.
  public saveDevAreaToBackend(): void {
    const userName = this.authService.getUserName();
    const modifiedRows = this.dataDevArea.filter(
      (row) => row.isModified || row.isNew
    );

    if (modifiedRows.length === 0) return;

    const requests = modifiedRows.map((row) =>
      this.tableService.insertOrUpdateDevArea(this.buildDevAreaPayload(row, userName))
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const successMessages: string[] = [];
          const errorMessages: string[] = [];

          res.forEach((r: any) => {
            const parsed = ErrorHandler.parseApiResult(r);
            if (parsed.success) {
              successMessages.push(parsed.message || 'Dev Area saved.');
            } else {
              errorMessages.push(parsed.message || 'Dev Area save failed.');
            }
          });

          if (successMessages.length > 0) {
            this.toastr.success(successMessages.join('\n'));
          }

          if (errorMessages.length > 0) {
            this.toastr.error(errorMessages.join('\n'));
          }

          this.loadDevAreaData();
        },
        error: (err) => {
          const parsed = ErrorHandler.parseApiResult(err?.error || err);
          this.toastr.error(parsed.message || 'Failed to save Development Areas.');
        },
      });
  }
  // This method saves the facility type data to the backend, handling both new and modified rows.
  public saveFacilityTypeToBackend(): void {
    const userName = this.authService.getUserName();
    const modifiedRows = this.dataFacilityType.filter(
      (row) => row.isModified || row.isNew
    );
    if (modifiedRows.length === 0) return;

    const requests = modifiedRows.map((row) =>
      this.tableService.insertOrUpdateFacilityType({
        existingFacilityTypeName: row.isNew
          ? ''
          : row.originalFacilityTypeName || row.type,
        newFacilityTypeName: row.type,
        surfaceFacilityTypeCreatedBy: row.createdBy,
        userName,
      })
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const successMessages: string[] = [];
          const errorMessages: string[] = [];

          res.forEach((r: any) => {
            const parsed = ErrorHandler.parseApiResult(r);
            if (parsed.success) {
              successMessages.push(parsed.message || 'Facility Type saved.');
            } else {
              errorMessages.push(parsed.message || 'Facility Type save failed.');
            }
          });

          if (successMessages.length > 0) {
            this.toastr.success(successMessages.join('\n'));
          }

          if (errorMessages.length > 0) {
            this.toastr.error(errorMessages.join('\n'));
          }

          this.loadFacilityTypeData();
        },
        error: (err) => {
          const parsed = ErrorHandler.parseApiResult(err?.error || err);
          this.toastr.error(parsed.message || 'Failed to save Facility Types.');
        },
      });
  }
  // This method saves the facility name data to the backend, handling both new and modified rows.
  public saveFacilityNameToBackend(): void {
    const userName = this.authService.getUserName();
    const modifiedRows = this.dataFacilityName.filter(
      (row) => row.isModified || row.isNew
    );

    if (modifiedRows.length === 0) return;

    const requests = modifiedRows.map((row) => {
      const payload = {
        existingFacilityName: row.originalFacilityName ?? null,
        newFacilityName: row.facilityName,
        facilityTypeName: row.type,
        developmentArea: row.developmentArea,
        userName,
      };

      row.updatedBy = userName;
      return this.tableService.insertOrUpdateFacilityName(payload);
    });

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const successMessages: string[] = [];
          const errorMessages: string[] = [];

          res.forEach((r: any) => {
            const parsed = ErrorHandler.parseApiResult(r);
            if (parsed.success) {
              successMessages.push(parsed.message || 'Facility Name saved.');
            } else {
              errorMessages.push(parsed.message || 'Facility Name save failed.');
            }
          });

          if (successMessages.length > 0) {
            this.toastr.success(successMessages.join('\n'));
          }

          if (errorMessages.length > 0) {
            this.toastr.error(errorMessages.join('\n'));
          }

          this.loadFacilityNameData();
        },
        error: (err) => {
          const parsed = ErrorHandler.parseApiResult(err?.error || err);
          this.toastr.error(parsed.message || 'Failed to save Facility Names.');
        },
      });
  }
  // This method retrieves the value of a cell in a row based on the specified field.
  public getCellValue(row: any, field: string): any {
    if (field === 'isActive') {
      return row[field] === 1 ? 'Active' : 'Inactive';
    }

    return row[field];
  }
  // This method provides the visual state class for the development area status pill.
  public getStatusClass(value: number | null | undefined): string {
    return value === 1 ? 'status-active' : 'status-inactive';
  }
  public getStatusHoverMessage(row: DevArea): string {
    const devAreaName = row.developmentArea || 'this development area';

    if (row.isStatusUpdating) {
      return `Updating ${devAreaName} status...`;
    }

    return row.isActive === 1
      ? `Click to make ${devAreaName} inactive`
      : `Click to make ${devAreaName} active`;
  }
  // This method sets the value of a cell in a row based on the specified field and type, and handles the edit operation.
  public setCellValue(row: any, field: string, value: any, type: string): void {
    row[field] = value;
    this.handleEdit(row, type);
    row.isModified = true;
  }
  // This method toggles the selection of a row based on the specified table type and event.
  public toggleRowSelection(row: any, table: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (table === 'dev') {
      this.selectedRowsDev = isChecked
        ? [...this.selectedRowsDev, row]
        : this.selectedRowsDev.filter((r) => r !== row);
    }
    if (table === 'type') {
      this.selectedRowsType = isChecked
        ? [...this.selectedRowsType, row]
        : this.selectedRowsType.filter((r) => r !== row);
    }
  }

  public async exportAdminTable(type: string): Promise<void> {
    const tabName = this.adminExportTabNames[type];
    if (!tabName) {
      return;
    }

    try {
      const { blob, fileName } = await this.tableService.exportDataByTabName(tabName, tabName);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }
}
